import { injectable } from "inversify";
import merge from "lodash/merge";
import { JsonParseError, JsonParseErrorCode } from "sparkson";
import { PersonalizerImpl } from "./Personalizer";

export interface AnimationPersonalizer {
  personalizeAnim(animationJSON: any, customAttributes: any, personalizationJSON: any): any;
}

@injectable()
export class AnimationPersonalizerImpl extends PersonalizerImpl implements AnimationPersonalizer {
  personalizeAnim(animationJSON: any, customAttributes: { [attr: string]: string }, personalizationJSON: any): any {
    if (customAttributes) {
      this.updateAnimJSON(animationJSON, this.personalize(customAttributes, personalizationJSON));
      return animationJSON;
    }

    return animationJSON;
  }

  private updateAnimJSON(animationItem: any, customAttributes: { [key: string]: string }) {
    const values = Object.keys(customAttributes).map((key) => JSON.parse(this.escapeBreakingChars(customAttributes[key])));

    let globalExpr = "";

    for (const value of values) {
      for (const valItem of value) {
        switch (valItem.type) {
          case "letiant":
            break;
          case "global":
            if (valItem.init) {
              globalExpr += valItem.init + ";";
            }
            break;
          default:
            // appease linter
            break;
        }
      }
    }

    for (const value of values) {
      for (const valItem of value) {
        const layers = this.findLayersByName(animationItem, valItem.name);
        for (const layer of layers) {
          if (valItem.path) {
            this.personalizeLayer(animationItem, layer, valItem, globalExpr);
          } else {
            this.personalizeLayerLegacy(animationItem, layer, valItem, globalExpr);
          }
        }
      }
    }
  }

  private escapeBreakingChars(parse: string): string {
    return parse.replace(/([^\\])\t/g, "$1\\t");
  }

  private updateText(layer: any, value: any, globalExpr: string) {
    if (value.val) {
      layer.t.d.k[0].s.t = this.evalMethodSelector(globalExpr + value.val);
    }
  }

  private updateShapeLegacy(layer: any, value: any, globalExpr: string) {
    if (value.init) {
      merge(layer, this.evalMethodSelector(globalExpr + value.init));
    }
  }

  private updateImage(animationItem: any, layer: any, rawSrc: string, globalExpr: string) {
    if (rawSrc) {
      const src = this.evalMethodSelector(globalExpr + rawSrc);
      const asset = this.findAssetByRefId(animationItem, layer.refId);
      const pathLength = src.lastIndexOf("/");
      asset.u = src.substr(0, pathLength) + "/";
      asset.p = src.substr(pathLength + 1, src.length);
    }
  }

  private findLayersByName(animationItem: any, name: string): any[] {
    const layers = [];
    const elements = animationItem.layers;
    for (const element of elements) {
      if (element.nm === name) {
        layers.push(element);
      }
    }
    return layers;
  }

  private findAssetByRefId(animationItem: any, refId: string) {
    let asset = null;
    const elements = animationItem.assets;
    for (const element of elements) {
      if (element.id === refId) {
        asset = element;
        break;
      }
    }
    return asset;
  }

  private evalMethodSelector(expr: string) {
    /* eslint-disable-next-line no-eval */
    return eval(expr);
  }

  private personalizeLayerLegacy(animationItem: any, layer: any, valItem: any, globalExpr: string) {
    if (valItem.position) {
      if (valItem.keyFrameNo === undefined) {
        layer.ks.p.k = this.evalMethodSelector(globalExpr + valItem.position);
      } else {
        let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.position);
        evaluatedValue = evaluatedValue.length === 2 ? [...evaluatedValue, 0] : evaluatedValue;
        layer.ks.p.k[valItem.keyFrameNo].s = evaluatedValue;
      }
    }
    if (valItem.scale) {
      let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.scale);
      evaluatedValue = typeof evaluatedValue === "number" ? [evaluatedValue, evaluatedValue] : evaluatedValue;
      if (valItem.keyFrameNo === undefined) {
        layer.ks.s.k = evaluatedValue;
      } else {
        layer.ks.s.k[valItem.keyFrameNo].s = evaluatedValue;
      }
    }
    if (valItem.anchor) {
      if (valItem.keyFrameNo === undefined) {
        layer.ks.a.k = this.evalMethodSelector(globalExpr + valItem.anchor);
      } else {
        let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.anchor);
        evaluatedValue = evaluatedValue.length === 2 ? [...evaluatedValue, 0] : evaluatedValue;
        layer.ks.a.k[valItem.keyFrameNo].s = evaluatedValue;
      }
    }
    if (valItem.opacity) {
      if (valItem.keyFrameNo === undefined) {
        layer.ks.o.k = this.evalMethodSelector(globalExpr + valItem.opacity);
      } else {
        let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.opacity);
        evaluatedValue = evaluatedValue instanceof Array ? evaluatedValue : [evaluatedValue];
        layer.ks.o.k[valItem.keyFrameNo].s = evaluatedValue;
      }
    }
    if (valItem.rotation) {
      if (valItem.keyFrameNo === undefined) {
        layer.ks.r.k = this.evalMethodSelector(globalExpr + valItem.rotation);
      } else {
        let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.rotation);
        evaluatedValue = evaluatedValue instanceof Array ? evaluatedValue : [evaluatedValue];
        layer.ks.r.k[valItem.keyFrameNo].s = evaluatedValue;
      }
    }

    switch (valItem.type) {
      case "text":
        this.updateText(layer, valItem, globalExpr);
        break;
      case "shape":
        this.updateShapeLegacy(layer, valItem, globalExpr);
        break;
      case "image":
        this.updateImage(animationItem, layer, valItem.src, globalExpr);
        break;
      default:
        // appease linter
        break;
    }
  }

  private personalizeLayer(animationItem: any, layer: any, valItem: any, globalExpr: string) {
    if (!valItem.path) {
      throw new JsonParseError(`Invalid animation configuration (path attribute is missing)`, JsonParseErrorCode.MISSING_PROPERTY);
    }
    const pathArray: string[] = valItem.path.split("|");

    if (pathArray[0] === "Text") {
      this.updateText(layer, valItem, globalExpr);
    } else if (pathArray[0] === "Transform") {
      this.updateTransform(pathArray[1], layer, valItem, globalExpr);
    } else if (pathArray[0] === "Effects") {
      this.updateEffect(pathArray, layer, valItem, globalExpr);
    } else if (pathArray[0] === "Image") {
      this.updateImage(animationItem, layer, valItem.val, globalExpr);
    } else if (pathArray[0] === "Contents") {
      this.updateShape(pathArray, layer, valItem, globalExpr);
    }
  }

  private updateTransform(transformType: string, layer: any, valItem: any, globalExpr: string) {
    switch (transformType) {
      case "Anchor Point":
        this.setMultiDimensionalValue(layer.ks.a, valItem, globalExpr);
        break;
      case "Position":
        this.setMultiDimensionalValue(layer.ks.p, valItem, globalExpr);
        break;
      case "Scale":
        this.setMultiDimensionalValue(layer.ks.s, valItem, globalExpr);
        break;
      case "Rotation":
        this.setValue(layer.ks.r, valItem, globalExpr);
        break;
      case "Opacity":
        this.setValue(layer.ks.o, valItem, globalExpr);
        break;
      case "Position X":
        this.setValue(layer.ks.px, valItem, globalExpr);
        break;
      case "Position Y":
        this.setValue(layer.ks.py, valItem, globalExpr);
        break;
      case "Position Z":
        this.setValue(layer.ks.pz, valItem, globalExpr);
        break;
      case "Skew":
        this.setValue(layer.ks.sk, valItem, globalExpr);
        break;
      case "Skew Axis":
        this.setValue(layer.ks.sa, valItem, globalExpr);
        break;
      default:
        throw new JsonParseError(
          `Invalid animation configuration (transform ` + transformType + ` not supported)`,
          JsonParseErrorCode.MISSING_PROPERTY,
        );
    }
  }

  private updateEffect(pathArray: string[], layer: any, valItem: any, globalExpr: string) {
    let jsonElement = layer.ef;
    for (let i = 1; i < pathArray.length; i++) {
      jsonElement = this.findJSONElementByName(jsonElement, pathArray[i]);
      if (!jsonElement) {
        throw new JsonParseError(
          `Invalid animation configuration (node ` + pathArray[i] + ` not found in animation json)`,
          JsonParseErrorCode.MISSING_PROPERTY,
        );
      }
    }

    // console.log(jsonElement);
    switch (jsonElement.ty) {
      case 0:
      case 1:
      case 2:
      case 7:
        this.setValue(jsonElement.v, valItem, globalExpr);
        break;
      default:
        throw new JsonParseError(
          `Invalid animation configuration (unsupported effect type ` + jsonElement.ty + `)`,
          JsonParseErrorCode.MISSING_PROPERTY,
        );
    }
  }

  private findJSONElementByName(jsonElement: any, name: string) {
    for (const attr in jsonElement) {
      if (jsonElement.hasOwnProperty(attr)) {
        const value = jsonElement[attr];

        if (typeof value === "object") {
          const foundedJsonElement = this.findJSONElementByName(value, name);
          if (foundedJsonElement) {
            return foundedJsonElement;
          }
        } else if (attr === "nm" && value === name) {
          return jsonElement;
        }
      }
    }

    return null;
  }

  private setMultiDimensionalValue(jsonElement: any, valItem: any, globalExpr: string) {
    if (valItem.keyFrameNo === undefined) {
      jsonElement.k = this.evalMethodSelector(globalExpr + valItem.val);
    } else {
      let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.val);
      evaluatedValue = evaluatedValue.length === 2 ? [...evaluatedValue, 0] : evaluatedValue;
      jsonElement.k[valItem.keyFrameNo].s = evaluatedValue;
    }
  }

  private setValue(jsonElement: any, valItem: any, globalExpr: string) {
    let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.val);
    if (valItem.keyFrameNo === undefined) {
      jsonElement.k = typeof jsonElement.k === "number" ? Number(evaluatedValue) : evaluatedValue;
    } else {
      // console.log(evaluatedValue + "," + globalExpr + valItem.val);
      evaluatedValue =
        evaluatedValue instanceof Array
          ? evaluatedValue
          : typeof jsonElement.k[valItem.keyFrameNo].s[0] === "number"
            ? [Number(evaluatedValue)]
            : [evaluatedValue];
      jsonElement.k[valItem.keyFrameNo].s = evaluatedValue;
      // console.log(jsonElement);
    }
  }

  private updateShape(pathArray: string[], layer: any, valItem: any, globalExpr: string) {
    let jsonElement = layer.shapes;
    for (let i = 1; i < pathArray.length - 1; i++) {
      if (pathArray[i] !== "Contents") {
        jsonElement = this.findJSONElementByName(jsonElement, pathArray[i]);
        if (!jsonElement) {
          throw new JsonParseError(
            `Invalid animation configuration (node ` + pathArray[i] + ` not found in animation json)`,
            JsonParseErrorCode.MISSING_PROPERTY,
          );
        }
      }
    }

    // console.log(jsonElement);
    const lastPath = pathArray[pathArray.length - 1];
    const transformMap = {
      el: {
        Position: jsonElement.p,
        Size: jsonElement.s,
      },
      fl: {
        Opacity: jsonElement.o,
        Color: jsonElement.c,
      },
      gf: {
        Opacity: jsonElement.o,
        "Start Point": jsonElement.s,
        "End  Point": jsonElement.e,
        Type: jsonElement.t,
        "Highlight Length": jsonElement.h,
        "Highlight Angle": jsonElement.a,
      },
      gs: {
        Opacity: jsonElement.o,
        "Start Point": jsonElement.s,
        "End  Point": jsonElement.e,
        Type: jsonElement.t,
        "Highlight Length": jsonElement.h,
        "Highlight Angle": jsonElement.a,
        "Stroke Width": jsonElement.w,
        "Line Cap": jsonElement.lc,
        "Line Join": jsonElement.lj,
        "Miter Limit": jsonElement.ml,
      },
      tm: {
        Start: jsonElement.s,
        End: jsonElement.e,
        Offset: jsonElement.o,
      },
      rc: {
        Position: jsonElement.p,
        Size: jsonElement.s,
        "Rounded corners": jsonElement.r,
      },
      rp: {
        Copies: jsonElement.c,
        Offset: jsonElement.o,
        Composite: jsonElement.m,
      },
      rd: {
        Radius: jsonElement.r,
      },
      sh: {
        Vertices: jsonElement.ks,
      },
      sr: {
        Position: jsonElement.p,
        "Inner Radius": jsonElement.ir,
        "Inner Roundness": jsonElement.is,
        "Outer Radius": jsonElement.or,
        "Outer Roundness": jsonElement.os,
        Rotation: jsonElement.r,
        Points: jsonElement.pt,
        "Star Type": jsonElement.sy,
      },
      st: {
        "Line Cap": jsonElement.lc,
        "Line Join": jsonElement.lj,
        "Miter Limit": jsonElement.ml,
        Opacity: jsonElement.o,
        Width: jsonElement.w,
        Color: jsonElement.c,
      },
    };

    if (!transformMap.hasOwnProperty(jsonElement.ty)) {
      throw new JsonParseError(
        `Invalid animation configuration (unsupported shape type ` + jsonElement.ty + `)`,
        JsonParseErrorCode.MISSING_PROPERTY,
      );
    }
    if (!transformMap[jsonElement.ty].hasOwnProperty(lastPath)) {
      throw new JsonParseError(
        `Invalid animation configuration (unsupported shape property ` + lastPath + ` for shape type ` + jsonElement.ty + `)`,
        JsonParseErrorCode.MISSING_PROPERTY,
      );
    }

    this.setValue(transformMap[jsonElement.ty][lastPath], valItem, globalExpr);
  }
}
